import {
  addRoute,
  Archived,
  type Airport,
  keepVariant,
  countryName,
  formatRange,
  modes,
  searchPlaces,
  type Mode,
  type MoneyRange,
  type Plan,
  type RouteLeg,
  type RoutePart,
  type RouteSearch,
  type Variants,
} from '@trippath/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { minutesSaid } from '../../lib/clock';
import { apart, byRoad } from '../../lib/map';
import { alongRoad, groundTime, useRoads, wayAsk, wayKey } from '../../lib/roads';
import { PAUSE } from '../place/TownField';
import { useReadOnly } from '../../app/readOnly';

/** The query with part `at` replaced by `code`, every airport already known by its code, a town
 * by its name as typed. */
function replaced(parts: RoutePart[], at: number, code: string): string {
  return parts
    .map((p, i) => (i === at ? code : p.place && !p.place.town ? p.place.code : p.text))
    .join('-');
}

/** What a leg should cost by its way, as the server prices it (places.rs `estimate`). */
export function legEstimate(leg: RouteLeg, mode: Mode): MoneyRange {
  const per = (min: number, max: number): MoneyRange => ({
    min: { ...leg.estimate.min, minor: Math.max(300, Math.trunc((leg.km * min) / 100)) },
    max: { ...leg.estimate.max, minor: Math.max(800, Math.trunc((leg.km * max) / 100)) },
  });
  if (mode === 'train') return per(5, 12);
  if (mode === 'bus') return per(3, 7);
  if (mode === 'ferry') return per(8, 20);
  if (mode === 'car') return per(6, 15);
  return leg.estimate;
}

/** A day in milliseconds. */
const DAY = 86_400_000;

/** How a leg goes unless said: on the ground to or from a town without an airport, else flown. */
export function defaultWay(leg: RouteLeg): Mode {
  return leg.from.town || leg.to.town ? 'bus' : 'flight';
}

/**
 * The nights at a leg's end unless said: a place a link changes at stays the nights between its
 * flights (one night in Helsinki from a flight in on the 19th and out on the 20th), else three.
 */
export function defaultNights(flights: RouteSearch['flights'], leg: RouteLeg): number {
  const arrive = flights.find((f) => f.to === leg.to.code);
  const leave = flights.find((f) => f.from === leg.to.code);
  return arrive && leave
    ? Math.round((Date.parse(leave.day) - Date.parse(arrive.day)) / DAY)
    : 3;
}

/**
 * How a route goes into the plan, as the server puts it (variants.rs `branch`): from the plan's
 * last stop its first place is, back in at a later stop its last place is, the stops between
 * left out; from no stop, on after the end.
 */
export function routeEffect(
  stops: readonly { id: string; city: string }[],
  route: Pick<RouteSearch, 'legs'>,
): { from: number | null; rejoin: number | null; dropped: string[] } {
  const same = (a: string, b: string | undefined) => a.toLowerCase() === b?.toLowerCase();
  const first = route.legs[0]?.from.city;
  const last = route.legs.at(-1)?.to.city;
  const at = stops.findLastIndex((s) => same(s.city, first));
  if (at < 0) return { from: null, rejoin: null, dropped: [] };
  const later = stops.findIndex((s, i) => i > at && same(s.city, last));
  const rejoin = later < 0 ? null : later;
  return {
    from: at,
    rejoin,
    dropped: stops.slice(at + 1, rejoin ?? stops.length).map((s) => s.city),
  };
}

/**
 * Whether text is a link from elsewhere than Google Flights, which names no places to read — a
 * bus company's search names its stops by number — and so is not searched.
 */
export function otherLink(text: string): boolean {
  const typed = text.trim();
  if (!/^https?:\/\//.test(typed)) return false;
  try {
    const url = new URL(typed);
    return !(url.hostname.includes('google.') && url.pathname.startsWith('/travel/flights'));
  } catch {
    return true;
  }
}

/** A route searched, as it is kept or added unless said otherwise: its places, ways and nights. */
export function routeWays(route: RouteSearch, typed: string) {
  return {
    codes: [
      ...route.legs.map((l) => l.from.code),
      ...route.legs.slice(-1).map((l) => l.to.code),
    ],
    modes: route.legs.map(defaultWay),
    nights: route.legs.map((leg) => defaultNights(route.flights, leg)),
    link: /^https?:\/\//.test(typed.trim()) ? typed.trim() : null,
  };
}

const icons: Record<Mode, string> = {
  flight: '✈',
  train: '🚆',
  bus: '🚌',
  ferry: '⛴',
  car: '🚗',
  foot: '🥾',
};

/** No place picked on the map. */
const NO_PINS: readonly string[] = [];

interface Props {
  plan: Plan;
  query: string;
  onQuery: (query: string) => void;
  /** The route searched, for the map to draw as a draft; `null` when there is none. */
  onRoute: (route: RouteSearch | null) => void;
  onAdded: (plan: Plan, cities: string[]) => void;
  /** The middle of the map: places offered are put nearest first. */
  near?: { lat: number; lon: number } | null;
  /** Told the way of each leg of the draft, for the map to draw a road along its road. */
  onWays?: (modes: Mode[]) => void;
  /** Told the variants once the draft is kept as one, and what it is called. */
  onKept?: (variants: Variants, name: string) => void;
  /** Places picked on the map, by name and point: `Dover@51.1279,1.3134`. */
  pins?: readonly string[];
}

/**
 * Where next, on the plan's map: `HEL-BKK`, a city, or a Google Flights link becomes a draft
 * route — each leg by plane, train, bus or ferry, each place for its nights — added to the plan
 * in one step.
 */
export function PlanSearch({
  plan,
  query,
  onQuery,
  onRoute,
  onAdded,
  near = null,
  onWays,
  onKept,
  pins = NO_PINS,
}: Props) {
  const { t, i18n } = useTranslation();
  const locked = useReadOnly();
  const lang = i18n.language;
  const ids = useId();
  const [answer, setAnswer] = useState<{ query: string; route: RouteSearch } | null>(null);
  // Each leg's way and each place's nights, by the leg and the place, so a new search keeps them.
  const [ways, setWays] = useState<Record<string, Mode>>({});
  const [nights, setNights] = useState<Record<string, number>>({});
  // What went wrong, said: no answer, or a trip put away read only meanwhile.
  const [failed, setFailed] = useState<'trips.failed' | 'trips.archivedNote' | null>(null);
  const refused = (error: unknown) => {
    setFailed(error instanceof Archived ? 'trips.archivedNote' : 'trips.failed');
  };
  // The query whose route the map draws: kept while the person goes on typing from it.
  const drawn = useRef('');

  useEffect(() => {
    if (query.trim() === '' || otherLink(query)) return;
    const stop = new AbortController();
    // Asked once typing pauses: a word that is no airport's city is looked up in OpenStreetMap.
    const timer = setTimeout(() => {
      searchPlaces(query, stop.signal, pins).then(onFound, onFailed);
    }, PAUSE);
    const onFound = (found: RouteSearch) => {
      setAnswer({ query, route: found });
      setFailed(null);
      if (found.legs.length > 0) {
        drawn.current = query;
        onRoute(found);
      } else if (!query.startsWith(drawn.current) || drawn.current === '') {
        drawn.current = '';
        // A place found before the route is whole is still shown where it is.
        onRoute(found.parts.some((p) => p.place) ? found : null);
      }
    };
    const onFailed = () => {
      setFailed(stop.signal.aborted ? null : 'trips.failed');
    };
    return () => {
      clearTimeout(timer);
      stop.abort();
    };
  }, [query, onRoute, pins]);

  const route = query.trim() !== '' && answer?.query === query ? answer.route : null;
  const parts = route?.parts ?? [];
  const legs = route?.legs ?? [];
  const last = parts.length - 1;
  const helped = parts[last];
  const before = parts.at(-2)?.place;
  let title = '';
  if (helped?.kind === 'fix') title = t('search.didYouMean');
  else if (helped?.kind === 'prefix') title = t('search.places');
  else if (helped?.kind === 'several')
    title = t('search.airports', { city: helped.place?.city, count: helped.options.length });
  else if (helped?.kind === 'empty' && before) title = t('search.from', { city: before.city });
  // One place found on its own is offered too: picked, the route goes on from it.
  const alone = helped?.kind === 'exact' && last === 0 ? helped.place : null;
  if (alone) title = t('search.places');
  let options: Airport[] = [];
  if (alone) options = [alone];
  else if (helped && title !== '') options = helped.options;
  // Where a word or the next hop is being chosen, the places nearest the map's middle first.
  const offered =
    near !== null && (helped?.kind === 'prefix' || helped?.kind === 'empty')
      ? options.toSorted((a, b) => apart(a, near) - apart(b, near))
      : options;

  // A place not found is said; one to pick is offered by the list itself, not said again.
  const hint = parts.some((p) => p.kind === 'none') ? t('search.hintNone') : '';

  const legKey = (leg: RouteLeg) => `${leg.from.code}-${leg.to.code}`;
  // A leg to or from a town without an airport goes on the ground.
  const wayOf = (leg: RouteLeg): Mode => ways[legKey(leg)] ?? defaultWay(leg);
  const legModes = legs.map(wayOf);
  const modesKey = legModes.join(' ');
  const roads = useRoads(
    legs.flatMap((leg, at) =>
      byRoad(legModes[at]) ? [wayAsk(leg.from, leg.to, legModes[at])] : [],
    ),
  );
  useEffect(() => {
    onWays?.(modesKey.split(' ').flatMap((word) => modes.filter((mode) => mode === word)));
  }, [modesKey, onWays]);
  // How far and how long: a flight as estimated, on the ground along the road when it is known.
  const legFacts = (leg: RouteLeg) => {
    const mode = wayOf(leg);
    const road = alongRoad(mode, roads.get(wayKey(leg.from, leg.to, mode)));
    const time =
      mode === 'flight'
        ? { minutes: leg.minutes, rough: !leg.direct }
        : groundTime(mode, leg.km, road);
    // By ferry, the crossing's own length.
    let km = leg.km;
    if (road) km = mode === 'ferry' ? road.ferryKm : road.km;
    // By road over a sea, the car ferry it takes: Tallinn to Helsinki goes on one.
    const ferry =
      road && mode !== 'ferry' && road.ferryKm > 0
        ? ` · ${t('search.withFerry', { km: road.ferryKm.toLocaleString(lang) })}`
        : '';
    return `${t('search.km', { km: km.toLocaleString(lang) })} · ${(time.rough ? '~' : '') + minutesSaid(time.minutes)}${ferry}`;
  };
  const flights = route?.flights ?? [];
  const nightsOf = (leg: RouteLeg) => nights[legKey(leg)] ?? defaultNights(flights, leg);
  // The link's fare is the first leg it flies, the rest of its flights in it — as it is added.
  const flies = (leg: RouteLeg) =>
    wayOf(leg) === 'flight' &&
    flights.some((f) => f.from === leg.from.code && f.to === leg.to.code);
  const fare = route?.price?.currency === 'EUR' ? route.price : null;
  const fared = fare === null ? -1 : legs.findIndex(flies);
  const costOf = (leg: RouteLeg, at: number): MoneyRange => {
    if (fare === null || !flies(leg)) return legEstimate(leg, wayOf(leg));
    const paid = { ...fare, minor: at === fared ? fare.minor : 0 };
    return { min: paid, max: paid };
  };
  const estimates = legs.map(costOf);
  const total = estimates.reduce<MoneyRange>(
    (sum, range) => ({
      min: { ...sum.min, minor: sum.min.minor + range.min.minor },
      max: { ...sum.max, minor: sum.max.minor + range.max.minor },
    }),
    { min: { currency: 'EUR', minor: 0 }, max: { currency: 'EUR', minor: 0 } },
  );
  const start = legs[0]?.from;
  const effect = routeEffect(plan.stops, { legs });
  const joined = effect.from !== null;
  // A draft that leaves stops out, or goes another way between two of them, is a proposal: a
  // branch kept as a variant, compared, then used — never merged into the plan by itself.
  const proposal = effect.dropped.length > 0 || effect.rejoin !== null;

  const codes = [...legs.map((l) => l.from.code), ...legs.slice(-1).map((l) => l.to.code)];
  const link = /^https?:\/\//.test(query.trim()) ? query.trim() : null;
  const keep = () => {
    const name = [...legs.slice(0, 1).map((l) => l.from.city), ...legs.map((l) => l.to.city)];
    keepVariant(
      plan.trip.id,
      codes,
      legs.map((leg) => wayOf(leg)),
      legs.map((leg) => nightsOf(leg)),
      link,
      null,
    ).then((kept) => {
      onKept?.(kept, name.join(' → '));
    }, refused);
  };
  const add = () => {
    addRoute(
      plan.trip.id,
      codes,
      legs.map((leg) => wayOf(leg)),
      legs.map((leg) => nightsOf(leg)),
      link,
    ).then((after) => {
      onAdded(
        after,
        legs.map((l) => l.to.city),
      );
    }, refused);
  };

  return (
    <section className="plan-search" aria-labelledby={`${ids}-label`}>
      <label className="visually-hidden" id={`${ids}-label`} htmlFor={`${ids}-input`}>
        {t('search.label')}
      </label>
      <div className="plan-search-box">
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-4-4" />
        </svg>
        <input
          id={`${ids}-input`}
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder={t('search.where')}
          value={query}
          onChange={(event) => {
            onQuery(event.target.value);
          }}
        />
        {query !== '' && (
          <button
            type="button"
            className="plan-search-clear"
            aria-label={t('search.clear')}
            onClick={() => {
              onQuery('');
            }}
          >
            ×
          </button>
        )}
      </div>

      {offered.length > 0 && (
        <div className="offered" role="group" aria-label={title}>
          {/* Places alone need no heading, as a search's suggestions have none; a fix or an
              airport's choice says what it is. */}
          {title !== t('search.places') && <p className="offered-title">{title}</p>}
          <ul>
            {offered.map((option) => (
              <li key={option.code}>
                <button
                  type="button"
                  className={helped?.kind === 'fix' ? 'option option-fix' : 'option'}
                  onClick={() => {
                    // The first place picked is followed by a dash: next, where to.
                    onQuery(replaced(parts, last, option.code) + (last === 0 ? '-' : ''));
                  }}
                >
                  {/* A search's suggestion: its mark under the search's own, its words on its line. */}
                  <svg
                    className="option-mark"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-4-4" />
                  </svg>
                  <span className="option-words">
                    <span className="option-city">{option.city}</span>
                    <span className="option-code">
                      {option.town ? <span aria-label={t('search.town')}>⌂</span> : option.code}
                    </span>
                    <span className="option-where">
                      {countryName(option.country, lang)} · {option.name}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hint !== '' && <p className="plan-search-hint">{hint}</p>}
      {failed !== null && (
        <p className="form-failed" role="alert">
          {t(failed)}
        </p>
      )}

      {route && legs.length > 0 && (
        <div className="draft" aria-label={t('search.draft')} role="group">
          <p className="draft-title">{t('search.draft')}</p>
          {route.flights.length > 0 && (
            <ul className="draft-flights" aria-label={t('search.fromLink')}>
              {route.flights.map((f) => (
                <li key={`${f.from}-${f.to}-${f.day}`}>
                  <span className="mono">{f.flight ?? '✈'}</span> {f.from} → {f.to} ·{' '}
                  {`${f.day.slice(8, 10)}.${f.day.slice(5, 7)}`}
                </li>
              ))}
            </ul>
          )}
          <ol className="draft-steps">
            {start && (
              <li className="draft-place draft-start">
                <span className="draft-dot" aria-hidden="true" />
                <span>
                  <strong>{start.city}</strong>
                  <span className="quiet">
                    {' · '}
                    {joined && t('search.inPlan')}
                    {!joined && plan.stops.length === 0 && t('search.firstStop')}
                    {!joined && plan.stops.length > 0 && t('search.notAdded')}
                  </span>
                </span>
              </li>
            )}
            {legs.map((leg, at) => (
              <li key={`${leg.from.code}-${leg.to.code}`} className="draft-leg-and-place">
                <div className="draft-leg">
                  <div
                    className="draft-ways"
                    role="group"
                    aria-label={t('search.way', { from: leg.from.city, to: leg.to.city })}
                  >
                    {modes.map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        aria-pressed={wayOf(leg) === mode}
                        aria-label={t(`mode.${mode}`)}
                        title={t(`mode.${mode}`)}
                        onClick={() => {
                          setWays((was) => ({ ...was, [legKey(leg)]: mode }));
                        }}
                      >
                        <span aria-hidden="true">{icons[mode]}</span>
                      </button>
                    ))}
                  </div>
                  <span className="draft-leg-facts quiet">
                    {legFacts(leg)} ·{' '}
                    <span className="mono">{formatRange(costOf(leg, at), lang, true)}</span>
                    {fare !== null && flies(leg) && ` · ${t('search.fromLinkFare')}`}
                  </span>
                </div>
                <div className="draft-place">
                  <span className="draft-dot" aria-hidden="true" />
                  <span className="draft-city">
                    <strong>{leg.to.city}</strong>
                    <span className="quiet"> · {countryName(leg.to.country, lang)}</span>
                  </span>
                  <span
                    className="draft-nights"
                    role="group"
                    aria-label={t('search.nightsAt', { city: leg.to.city })}
                  >
                    <button
                      type="button"
                      aria-label={t('search.fewer', { city: leg.to.city })}
                      disabled={nightsOf(leg) === 0}
                      onClick={() => {
                        setNights((was) => ({ ...was, [legKey(leg)]: nightsOf(leg) - 1 }));
                      }}
                    >
                      −
                    </button>
                    <span className="mono">{t('search.nights', { count: nightsOf(leg) })}</span>
                    <button
                      type="button"
                      aria-label={t('search.more', { city: leg.to.city })}
                      onClick={() => {
                        setNights((was) => ({ ...was, [legKey(leg)]: nightsOf(leg) + 1 }));
                      }}
                    >
                      +
                    </button>
                  </span>
                </div>
              </li>
            ))}
          </ol>
          <p className="draft-total">
            <span>{t('search.travel')}</span>
            <span className="mono">{formatRange(total, lang, true)}</span>
          </p>
          <p className="quiet draft-note">{t('search.typicalNote')}</p>
          <div className="draft-actions">
            <button
              type="button"
              className="button"
              onClick={() => {
                onQuery('');
              }}
            >
              {t('dialog.cancel')}
            </button>
            {locked ? (
              // A trip read only: the way looked at, not added.
              <p className="quiet">{t('trips.readOnlyNote')}</p>
            ) : plan.trip.currency === 'EUR' ? (
              <>
                <button
                  type="button"
                  className={proposal ? 'button button-primary' : 'button'}
                  onClick={keep}
                >
                  {t('variants.keep')}
                </button>
                {!proposal && (
                  <button type="button" className="button button-primary" onClick={add}>
                    {t('search.addToPlan')}
                  </button>
                )}
              </>
            ) : (
              <p className="quiet">{t('search.notEuro', { currency: plan.trip.currency })}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
